import React, { useState, useEffect } from "react";
import { Card, Table, Input, Button, Typography, Space, message, Tooltip, Select } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { ColumnConfigModal, ConfigField } from "../../common/ColumnConfigModal";
import { useTender } from "../../../contexts/TenderContext";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import NumberInput from "../../common/NumberInput";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { addColumnCodes } from "@/utils/addColumnCodes";
import { PLOT_SERVICE_REFERENCES } from "@/utils/plotServiceReferenceMapping";

const { Title, Text } = Typography;

interface PlotServiceRow {
  key: string;
  // Plot Services (Service Details)
  plots: number;
  apartments: number;
  eo25mmBarrierPipe: number;
  eo32mmBarrierPipe: number;
  virginMedia: number;
  garageDuct: number;
  privateStLightDuct: number;
  pccEdgingsGasBoxes: number;
  // Car Charging / Heat Pump
  ductingToChargingPoint: number;
  concBaseChargingPoints: number;
  installBollardBase: number;
  airSourceHeatPump: number;
  // Service Connection (separate fields)
  serviceConnectionPlots: number;
  serviceConnectionApartments: number;
  vacEx: number;
}

const excelColumns: ExcelColumn[] = [
  { key: "key", title: "Key" },
  // Plot Services
  { key: "plots", title: "Plot Services - Plots Nr" },
  { key: "apartments", title: "Plot Services - Apartments Nr" },
  { key: "eo25mmBarrierPipe", title: "EO for barrier pipes - 25mm (m)" },
  { key: "eo32mmBarrierPipe", title: "EO for barrier pipes - 32mm (m)" },
  { key: "virginMedia", title: "EO for Virgin Media duct installation Nr" },
  { key: "garageDuct", title: "Between house and garage Nr" },
  { key: "privateStLightDuct", title: "Private street lighting duct (m)" },
  { key: "pccEdgingsGasBoxes", title: "PCC Edgings to gas boxes Nr" },
  {
    key: "ductingToChargingPoint",
    title: "Ducting for car charging points (m)",
  },
  {
    key: "concBaseChargingPoints",
    title: "Concrete base for charging point Nr",
  },
  {
    key: "installBollardBase",
    title: "Install only bollard for charging point Nr",
  },
  { key: "airSourceHeatPump", title: "Base for Air Source Heat Pump Nr" },
  // Service Connection
  { key: "serviceConnectionPlots", title: "Plots Nr" },
  {
    key: "serviceConnectionApartments",
    title: "Apartments Nr",
  },
  { key: "vacEx", title: "Service Connection - Vac-ex Nr" },
];

const PlotService: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  // Format column title to display field name and unit separately
  const formatColumnTitle = React.useCallback(
    (baseTitle: string): React.ReactNode => {
      const unitMatch = baseTitle.match(/\s+\((m[²³]|m|Nr|t)\)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1];
        titleWithoutUnit = baseTitle.replace(/\s+\((m[²³]|m|Nr|t)\)$/, "");
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }
      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    [],
  );

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent =
    tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState(
    "groundworks_includePreliminariesDisplay",
    preliminariesPercent,
  );
  const includeOhp = getCheckboxState(
    "groundworks_includeOhpDisplay",
    ohpPercent,
  );
  const includeDiscount = getCheckboxState(
    "groundworks_includeDiscountDisplay",
    discountPercent,
  );

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<PlotServiceRow[]>([]);
  const [activeTab, setActiveTab] = useState<string>("plot-services");
  // Defers the Plot Services / Service Connections tables' mount by one
  // paint cycle so a tender with a lot of plots doesn't make this step look
  // frozen while the table's render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Re-keyed on `activeTab` since
  // switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the tables render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);
  const [tableKey, setTableKey] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedServiceDetailsKeys, setSelectedServiceDetailsKeys] = useState<React.Key[]>([]);
  const [selectedServiceConnectionKeys, setSelectedServiceConnectionKeys] = useState<React.Key[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [tempApartmentsMultiple, setTempApartmentsMultiple] = useState<number>(
    tender.groundworks?.plotServiceConfig?.plotServicesApartmentsMultiple ?? 1,
  );

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<PlotServiceRow[]>({
    pageId: "plot-service",
    onChangeDetected: (changes) => {
    },
  }); */

  // Sync temp value when modal opens
  React.useEffect(() => {
    if (configModalVisible) {
      setTempApartmentsMultiple(
        tender.groundworks?.plotServiceConfig?.plotServicesApartmentsMultiple ?? 1,
      );
    }
  }, [configModalVisible]);

  // Force table re-render when switching tabs
  React.useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [activeTab]);

  useEffect(() => {
    if (tender?.groundworks?.plotServiceTable) {
      setData(tender.groundworks.plotServiceTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.plotServiceTable]);

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<PlotServiceRow>({
    filename: "PlotService",
    sheetName: "Plot Service",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => {
        const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);
        const merged = { ...emptyRow, ...row };

        // Round all numeric fields to 2 decimals
        Object.keys(merged).forEach((key) => {
          if (typeof merged[key] === "number" && key !== "key") {
            merged[key] = Math.round(merged[key] * 1000) / 1000;
          }
        });

        return merged;
      });
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        plotServiceTable: processedData,
      });
    },
    skipValidation: true,
  });

  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          // Real workbooks name this sheet "PLOT SERVICE QUANTS " (trailing
          // space), so match by substring rather than an exact string - an
          // exact match would silently fall back to the first sheet instead.
          const sheetName =
            workbook.SheetNames.find((name) =>
              name.toUpperCase().includes("PLOT SERVICE"),
            ) ?? workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];

          // Two-pass header row detection
          let headerRowIndex = -1;

          // Pass 1: row with "plots" AND another service keyword
          for (let i = 0; i < Math.min(15, jsonData.length); i++) {
            const row = jsonData[i] || [];
            const cells = row.map((c: any) =>
              String(c || "")
                .toLowerCase()
                .trim(),
            );
            const hasPlots = cells.some(
              (c) =>
                c === "plots" ||
                c === "houses" ||
                c.includes("excavate") ||
                c.includes("service trench"),
            );
            const hasOther = cells.some(
              (c) =>
                c === "apartments" ||
                c.includes("barrier") ||
                c.includes("vac-ex") ||
                c.includes("vacex") ||
                c.includes("virgin media") ||
                c.includes("bollard") ||
                c.includes("charging"),
            );
            if (hasPlots && hasOther) {
              headerRowIndex = i;
              break;
            }
          }

          // Pass 2: any row with 2+ service keywords
          if (headerRowIndex === -1) {
            for (let i = 0; i < Math.min(15, jsonData.length); i++) {
              const row = jsonData[i] || [];
              const rowText = row
                .map((c: any) => String(c || "").toLowerCase())
                .join(" ");
              const matchCount = [
                "barrier",
                "virgin",
                "bollard",
                "charging",
                "vac",
                "heat pump",
                "lighting",
              ].filter((kw) => rowText.includes(kw)).length;
              if (matchCount >= 2) {
                headerRowIndex = i;
                break;
              }
            }
          }

          if (headerRowIndex === -1) {
            alert("Could not find header row in Excel file.");
            return;
          }

          const headers = jsonData[headerRowIndex];

          const findColIdx = (keywords: string[]): number => {
            for (let i = 0; i < headers.length; i++) {
              const cell = String(headers[i] || "")
                .toLowerCase()
                .trim();
              if (keywords.every((kw) => cell.includes(kw.toLowerCase())))
                return i;
            }
            return -1;
          };

          const findAllColIdx = (keywords: string[]): number[] => {
            const result: number[] = [];
            for (let i = 0; i < headers.length; i++) {
              const cell = String(headers[i] || "")
                .toLowerCase()
                .trim();
              if (keywords.every((kw) => cell.includes(kw.toLowerCase())))
                result.push(i);
            }
            return result;
          };

          // "Houses" (formerly "Plots") and "Apartments" appear twice
          const plotsAllOld = findAllColIdx(["plots"]);
          const plotsAllNew = findAllColIdx(["houses"]);
          const plotsAll = plotsAllOld.length > 0 ? plotsAllOld : plotsAllNew;
          const plotsIdx = plotsAll[0] ?? -1;
          const serviceConnectionPlotsIdx = plotsAll[1] ?? -1;

          const apartmentsAll = findAllColIdx(["apartments"]);
          const apartmentsIdx = apartmentsAll[0] ?? -1;
          const serviceConnectionApartmentsIdx = apartmentsAll[1] ?? -1;

          // Single-occurrence columns
          const eo25mmIdx = findColIdx(["barrier", "25"]);
          const eo32mmIdx = findColIdx(["barrier", "32"]);
          const virginMediaIdx = findColIdx(["virgin"]);
          const garageDuctIdx =
            findColIdx(["garage"]) !== -1
              ? findColIdx(["garage"])
              : findColIdx(["house", "garage"]);
          const privateLightIdx =
            findColIdx(["lighting"]) !== -1
              ? findColIdx(["lighting"])
              : findColIdx(["light", "duct"]);
          const pccEdgingsIdx =
            findColIdx(["pcc"]) !== -1
              ? findColIdx(["pcc"])
              : findColIdx(["gas", "box"]);
          const ductingChargingIdx = findColIdx(["ducting"]);
          const concBaseIdx =
            findColIdx(["conc", "base"]) !== -1
              ? findColIdx(["conc", "base"])
              : findColIdx(["concrete", "base"]);
          const bollardIdx = findColIdx(["bollard"]);
          const airSourceHeatPumpIdx =
            findColIdx(["heat", "pump"]) !== -1
              ? findColIdx(["heat", "pump"])
              : findColIdx(["air", "source"]);
          const vacExIdx =
            findColIdx(["vac-ex"]) !== -1
              ? findColIdx(["vac-ex"])
              : findColIdx(["vac"]);

          const getValue = (row: any[], idx: number): number =>
            idx !== -1 ? Math.round((Number(row[idx]) || 0) * 1000) / 1000 : 0;

          const dataRows = jsonData.slice(headerRowIndex + 1);

          const allImportedRows: PlotServiceRow[] = dataRows
            .filter((row) => row && row.length > 0)
            .map((row, index) => ({
              key: `imported_${Date.now()}_${index}`,
              plots: getValue(row, plotsIdx),
              apartments: getValue(row, apartmentsIdx),
              eo25mmBarrierPipe: getValue(row, eo25mmIdx),
              eo32mmBarrierPipe: getValue(row, eo32mmIdx),
              virginMedia: getValue(row, virginMediaIdx),
              garageDuct: getValue(row, garageDuctIdx),
              privateStLightDuct: getValue(row, privateLightIdx),
              pccEdgingsGasBoxes: getValue(row, pccEdgingsIdx),
              ductingToChargingPoint: getValue(row, ductingChargingIdx),
              concBaseChargingPoints: getValue(row, concBaseIdx),
              installBollardBase: getValue(row, bollardIdx),
              airSourceHeatPump: getValue(row, airSourceHeatPumpIdx),
              serviceConnectionPlots: getValue(row, serviceConnectionPlotsIdx),
              serviceConnectionApartments: getValue(
                row,
                serviceConnectionApartmentsIdx,
              ),
              vacEx: getValue(row, vacExIdx),
            }))
            .filter((row) =>
              Object.entries(row).some(
                ([k, v]) => k !== "key" && typeof v === "number" && v > 0,
              ),
            )
            // Plot Services is a single aggregate row (no plot/house type
            // columns), so only the first row with real data is wanted -
            // trailing template rows in the sheet must not be imported too.
            .slice(0, 1);

          if (allImportedRows.length === 0) {
            alert("No valid data found to import.");
            return;
          }

          setData(allImportedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            plotServiceTable: allImportedRows,
          });
          alert(`Successfully imported ${allImportedRows.length} rows`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  const createEmptyRow = (key?: string): PlotServiceRow => ({
    key: key || Date.now().toString(),
    // Service Details
    plots: 0,
    apartments: 0,
    eo25mmBarrierPipe: 0,
    eo32mmBarrierPipe: 0,
    virginMedia: 0,
    garageDuct: 0,
    privateStLightDuct: 0,
    pccEdgingsGasBoxes: 0,
    // Car Charging / Heat Pump
    ductingToChargingPoint: 0,
    concBaseChargingPoints: 0,
    installBollardBase: 0,
    airSourceHeatPump: 0,
    // Service Connection
    serviceConnectionPlots: 0,
    serviceConnectionApartments: 0,
    vacEx: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof PlotServiceRow,
    value: string | number,
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;

      // Track the field change
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        plotServiceTable: newData,
      });
      return newData;
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      plotServiceTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedServiceDetailsRows = () => {
    setKeysToDelete(selectedServiceDetailsKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const deleteSelectedServiceConnectionRows = () => {
    setKeysToDelete(selectedServiceConnectionKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Plot Service Bill Summary — see CutFills.tsx's identical
      // fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        plotServiceTable: newData,
        ...(newData.length === 0 && { plotServiceExtraItems: {} }),
      });
      setSelectedServiceDetailsKeys([]);
      setSelectedServiceConnectionKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const calculateTotals = () => {
    const totals: Partial<PlotServiceRow> = {
      plots: 0,
      apartments: 0,
      eo25mmBarrierPipe: 0,
      eo32mmBarrierPipe: 0,
      virginMedia: 0,
      garageDuct: 0,
      privateStLightDuct: 0,
      pccEdgingsGasBoxes: 0,
      ductingToChargingPoint: 0,
      concBaseChargingPoints: 0,
      installBollardBase: 0,
      airSourceHeatPump: 0,
      serviceConnectionPlots: 0,
      serviceConnectionApartments: 0,
      vacEx: 0,
    };

    data.forEach((row) => {
      Object.keys(totals).forEach((key) => {
        totals[key] += Math.round((Number(row[key]) || 0) * 1000) / 1000;
      });
    });

    return totals;
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(
        tender,
        "Save and continue from Plot Service",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/plot-pavings"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(
        tender,
        "Save and exit from Plot Service",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const SERVICE_DETAILS_WIDTH = 2560;
  const SERVICE_CONNECTION_WIDTH = 450;

  const baseColumns = [];

  const serviceDetailsColumns = [
    ...baseColumns,
    {
      title: formatColumnTitle(
        "Excavate service trenches from back of footpath to plots, including supply and lay duct or 25mm Mdpe pipes for water, gas, electric and BT (free issue) (Nr)",
      ),
      dataIndex: "plots",
      key: "plots",
      width: 600,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "plots", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Apartments (Nr)"),
      dataIndex: "apartments",
      key: "apartments",
      width: 110,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "apartments", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("EO for barrier pipes - 25mmØ (m)"),
      dataIndex: "eo25mmBarrierPipe",
      key: "eo25mmBarrierPipe",
      width: 150,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "eo25mmBarrierPipe", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("EO for barrier pipes - 32mmØ (m)"),
      dataIndex: "eo32mmBarrierPipe",
      key: "eo32mmBarrierPipe",
      width: 150,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "eo32mmBarrierPipe", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("EO for Virgin Media duct installation (Nr)"),
      dataIndex: "virginMedia",
      key: "virginMedia",
      width: 200,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "virginMedia", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Between house and garage (Nr)"),
      dataIndex: "garageDuct",
      key: "garageDuct",
      width: 160,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "garageDuct", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Private street lighting duct (m)"),
      dataIndex: "privateStLightDuct",
      key: "privateStLightDuct",
      width: 170,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "privateStLightDuct", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("PCC Edgings to gas boxes (Nr)"),
      dataIndex: "pccEdgingsGasBoxes",
      key: "pccEdgingsGasBoxes",
      width: 170,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "pccEdgingsGasBoxes", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Ducting for car charging points (m)"),
      dataIndex: "ductingToChargingPoint",
      key: "ductingToChargingPoint",
      width: 180,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "ductingToChargingPoint", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Concrete base for charging point (Nr)"),
      dataIndex: "concBaseChargingPoints",
      key: "concBaseChargingPoints",
      width: 180,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "concBaseChargingPoints", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Install only bollard for charging point (Nr)"),
      dataIndex: "installBollardBase",
      key: "installBollardBase",
      width: 200,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "installBollardBase", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Base for Air Source Heat Pump (Nr)"),
      dataIndex: "airSourceHeatPump",
      key: "airSourceHeatPump",
      width: 180,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "airSourceHeatPump", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: PlotServiceRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  const serviceConnectionColumns = [
    ...baseColumns,
    {
      title: formatColumnTitle("Plots (Nr)"),
      dataIndex: "serviceConnectionPlots",
      key: "serviceConnectionPlots",
      width: 110,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "serviceConnectionPlots", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Apartments (Nr)"),
      dataIndex: "serviceConnectionApartments",
      key: "serviceConnectionApartments",
      width: 120,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(
              record.key,
              "serviceConnectionApartments",
              newValue,
            )
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Vac-ex (Nr)"),
      dataIndex: "vacEx",
      key: "vacEx",
      width: 110,
      render: (value: number, record: PlotServiceRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "vacEx", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: PlotServiceRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  const tabItems = [
    {
      key: "plot-services",
      label: "Plot Services",
      children: (
        <Table
          dataSource={data}
          columns={addColumnCodes(serviceDetailsColumns, PLOT_SERVICE_REFERENCES)}
          pagination={false}
          scroll={{ x: SERVICE_DETAILS_WIDTH }}
          size="middle"
          bordered
          className="plot-service-table"
          summary={() => {
            if (data.length === 0) return null;
            const totals = calculateTotals();
            const cell = (val: number | undefined) => (
              <span
                style={{
                  color: "#000",
                  display: "block",
                  textAlign: "right",
                  paddingRight: "8px",
                }}
              >
                {(val ?? 0).toFixed(3)}
              </span>
            );
            return (
              <Table.Summary>
                <Table.Summary.Row
                  style={{
                    backgroundColor: "#FFF7E6",
                    fontWeight: 600,
                    height: "60px",
                  }}
                >
                  <Table.Summary.Cell index={0} style={{ height: "60px" }}>
                    {cell(totals.plots)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {cell(totals.apartments)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {cell(totals.eo25mmBarrierPipe)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    {cell(totals.eo32mmBarrierPipe)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    {cell(totals.virginMedia)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    {cell(totals.garageDuct)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    {cell(totals.privateStLightDuct)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>
                    {cell(totals.pccEdgingsGasBoxes)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>
                    {cell(totals.ductingToChargingPoint)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9}>
                    {cell(totals.concBaseChargingPoints)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10}>
                    {cell(totals.installBollardBase)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11}>
                    {cell(totals.airSourceHeatPump)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={12}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      ),
    },
    {
      key: "service-connection",
      label: "Service Connections",
      children: (
        <Table
          dataSource={data}
          columns={addColumnCodes(serviceConnectionColumns, PLOT_SERVICE_REFERENCES)}
          pagination={false}
          scroll={{ x: SERVICE_CONNECTION_WIDTH }}
          size="middle"
          bordered
          className="service-connection-table"
          summary={() => {
            if (data.length === 0) return null;
            const totals = calculateTotals();
            const cell = (val: number | undefined) => (
              <span
                style={{
                  color: "#000",
                  display: "block",
                  textAlign: "right",
                  paddingRight: "8px",
                }}
              >
                {(val ?? 0).toFixed(3)}
              </span>
            );
            return (
              <Table.Summary>
                <Table.Summary.Row
                  style={{
                    backgroundColor: "#FFF7E6",
                    fontWeight: 600,
                    height: "60px",
                  }}
                >
                  <Table.Summary.Cell index={0} style={{ height: "60px" }}>
                    {cell(totals.serviceConnectionPlots)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {cell(totals.serviceConnectionApartments)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {cell(totals.vacEx)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      ),
    },
  ];

  // Calculate if horizontal and vertical scroll is needed
  const getScrollConfig = (minWidth: number) => {
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;

    const scrollConfig: any = { x: minWidth };

    if (needsVerticalScroll) {
      scrollConfig.y = maxComfortableRows * estimatedRowHeight;
    }

    return scrollConfig;
  };

  return (
    <MainLayout showFooter={false}>
      <Card
        className="shadow-sm plot-service-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title and Tabs */}
        <div
          className="plot-service-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Plot Service
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Configure detailed service connections including utilities for
                  each plot.
                </Text>
              </div>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalVisible(true)}
                style={{
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                  height: "32px",
                  padding: "0 16px",
                  fontWeight: 500,
                  fontSize: "14px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "18px",
                }}
              >
                Configure Headers
              </Button>
            </div>
          </div>

          {/* Tab Buttons */}
          <div
            className="plot-service-sticky-tabs px-6"
            style={{ paddingTop: "24px", paddingBottom: "0px" }}
          >
            <div style={{ marginBottom: "0px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              {/* Left side - Tab buttons */}
              <div style={{ display: "flex", gap: "4px" }}>
                <Button
                  type={activeTab === "plot-services" ? "primary" : "default"}
                  onClick={() => setActiveTab("plot-services")}
                  className={
                    activeTab === "plot-services"
                      ? "active-tab-button"
                      : "inactive-tab-button"
                  }
                  style={{
                    backgroundColor:
                      activeTab === "plot-services" ? COLORS.primary : "#E1E6EF",
                    borderColor:
                      activeTab === "plot-services" ? COLORS.primary : "#E1E6EF",
                    color: activeTab === "plot-services" ? "#fff" : "#6B7280",
                    fontWeight: 500,
                    borderRadius: "6px 6px 0 0",
                    height: "38px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    borderBottom:
                      activeTab === "plot-services"
                        ? "none"
                        : "1px solid #E1E6EF",
                    position: "relative",
                    zIndex: activeTab === "plot-services" ? 2 : 1,
                  }}
                >
                  Plot Services
                </Button>
                <Button
                  type={
                    activeTab === "service-connection" ? "primary" : "default"
                  }
                  onClick={() => setActiveTab("service-connection")}
                  className={
                    activeTab === "service-connection"
                      ? "active-tab-button"
                      : "inactive-tab-button"
                  }
                  style={{
                    backgroundColor:
                      activeTab === "service-connection"
                        ? COLORS.primary
                        : "#E1E6EF",
                    borderColor:
                      activeTab === "service-connection"
                        ? COLORS.primary
                        : "#E1E6EF",
                    color:
                      activeTab === "service-connection" ? "#fff" : "#6B7280",
                    fontWeight: 500,
                    borderRadius: "6px 6px 0 0",
                    height: "38px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    borderBottom:
                      activeTab === "service-connection"
                        ? "none"
                        : "1px solid #E1E6EF",
                    position: "relative",
                    zIndex: activeTab === "service-connection" ? 2 : 1,
                  }}
                >
                  Service Connection
                </Button>
              </div>
              {/* Right side - Delete Selected + Configure buttons */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                {activeTab === "plot-services" && selectedServiceDetailsKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedServiceDetailsRows}
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Delete Selected ({selectedServiceDetailsKeys.length})
                  </Button>
                )}
                {activeTab === "service-connection" && selectedServiceConnectionKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedServiceConnectionRows}
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Delete Selected ({selectedServiceConnectionKeys.length})
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 plot-service-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Plot Services Table */}
          {activeTab === "plot-services" && (isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
            <Table
              key={tableKey}
              dataSource={data}
              columns={addColumnCodes(serviceDetailsColumns, PLOT_SERVICE_REFERENCES)}
              pagination={false}
              rowSelection={{
                selectedRowKeys: selectedServiceDetailsKeys,
                onChange: setSelectedServiceDetailsKeys,
              }}
              scroll={getScrollConfig(SERVICE_DETAILS_WIDTH)}
              sticky={{
                offsetHeader:
                  144 +
                  document
                    .getElementsByClassName("plot-service-sticky-header")?.[0]
                    ?.getBoundingClientRect?.()?.height,
              }}
              size="middle"
              bordered
              className="plot-service-table"
              summary={() => {
                if (data.length === 0) return null;
                const totals = calculateTotals();
                const cell = (val: number | undefined) => (
                  <span
                    style={{
                      color: "#000",
                      display: "block",
                      textAlign: "right",
                      paddingRight: "8px",
                    }}
                  >
                    {(val ?? 0).toFixed(3)}
                  </span>
                );
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{
                        backgroundColor: "#FFF7E6",
                        fontWeight: 600,
                        height: "60px",
                      }}
                    >
                      <Table.Summary.Cell index={0} style={{ height: "60px" }}><span /></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        {cell(totals.plots)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        {cell(totals.apartments)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        {cell(totals.eo25mmBarrierPipe)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        {cell(totals.eo32mmBarrierPipe)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>
                        {cell(totals.virginMedia)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>
                        {cell(totals.garageDuct)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>
                        {cell(totals.privateStLightDuct)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8}>
                        {cell(totals.pccEdgingsGasBoxes)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9}>
                        {cell(totals.ductingToChargingPoint)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10}>
                        {cell(totals.concBaseChargingPoints)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11}>
                        {cell(totals.installBollardBase)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={12}>
                        {cell(totals.airSourceHeatPump)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={13}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          ))}

          {/* Service Connection Table */}
          {activeTab === "service-connection" && (isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
            <Table
              key={tableKey}
              dataSource={data}
              columns={addColumnCodes(serviceConnectionColumns, PLOT_SERVICE_REFERENCES)}
              pagination={false}
              rowSelection={{
                selectedRowKeys: selectedServiceConnectionKeys,
                onChange: setSelectedServiceConnectionKeys,
              }}
              scroll={getScrollConfig(SERVICE_CONNECTION_WIDTH)}
              sticky={{
                offsetHeader:
                  144 +
                  document
                    .getElementsByClassName("plot-service-sticky-header")?.[0]
                    ?.getBoundingClientRect?.()?.height,
              }}
              size="middle"
              bordered
              className="plot-service-table"
              summary={() => {
                if (data.length === 0) return null;
                const totals = calculateTotals();
                const cell = (val: number | undefined) => (
                  <span
                    style={{
                      color: "#000",
                      display: "block",
                      textAlign: "right",
                      paddingRight: "8px",
                    }}
                  >
                    {(val ?? 0).toFixed(3)}
                  </span>
                );
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{
                        backgroundColor: "#FFF7E6",
                        fontWeight: 600,
                        height: "60px",
                      }}
                    >
                      <Table.Summary.Cell index={0} style={{ height: "60px" }}><span /></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        {cell(totals.serviceConnectionPlots)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        {cell(totals.serviceConnectionApartments)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        {cell(totals.vacEx)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          ))}

          {/* Add New Row Button */}
          <div style={{ marginTop: "16px" }}>
            <Button
              type="text"
              icon={<PlusOutlined style={{ color: COLORS.primary }} />}
              onClick={addRow}
              style={{
                color: COLORS.primary,
                fontWeight: 500,
                padding: "4px 8px",
              }}
            >
              Add New Row
            </Button>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>

        {/* Excel Import Modal - Hidden, only for modal functionality */}
        <div style={{ display: "none" }}>
          <ExcelImportExport
            onExport={handleExportExcel}
            onImport={handleImportExcel}
            onDownloadTemplate={handleDownloadTemplate}
            importLoading={importLoading}
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
            hasData={data.length > 0}
          />
        </div>
      </Card>

      <style>{`
        .plot-service-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .plot-service-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .plot-service-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .plot-service-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .plot-service-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .plot-service-card .ant-card-body {
          overflow: visible !important;
        }

        .plot-service-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .plot-service-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .plot-service-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .plot-service-table .ant-table-wrapper {
          margin-top: -1px !important;
        }

        .plot-service-table .ant-table-container {
          margin-top: 0 !important;
        }

        .plot-service-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .plot-service-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .plot-service-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .plot-service-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .plot-service-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .plot-service-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .plot-service-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .plot-service-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .plot-service-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .plot-service-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude first 2 columns: Plot No, Housetype) */
        .plot-service-table td:not(:nth-child(1)):not(:nth-child(2)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .plot-service-table .ant-input[type="number"],
        .service-connection-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .plot-service-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .plot-service-table .ant-input-number {
          width: 100%;
        }

        .plot-service-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .plot-service-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .plot-service-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .plot-service-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      <ColumnConfigModal
        title="Configure"
        open={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        onApply={() => {
          updateField("groundworks", {
            ...tender.groundworks,
            plotServiceConfig: {
              plotServicesApartmentsMultiple: tempApartmentsMultiple,
              serviceConnectionApartmentsMultiple: tempApartmentsMultiple,
            },
          });
        }}
        width={400}
      >
        <ConfigField label="Apartments (multiple of plot rate)">
          <Select
            value={tempApartmentsMultiple}
            onChange={(val: number) => setTempApartmentsMultiple(val)}
            style={{ width: "100%" }}
            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
              value: n,
              label: String(n),
            }))}
          />
        </ConfigField>
      </ColumnConfigModal>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </MainLayout>
  );
};

export default PlotService;
